import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/src/reducers/types';

import Switch from '@/src/components/common/Switch/Switch';
import { queryClient } from '@/src/lib/react-query/react-query';

import { dashboardMembersKeys, DashboardMembersResponse } from '../../api/getDashboardMembers';
import { useUpdateProjectMemberDashboardVisibility } from '../../api/updateProjectMemberDashboardVisibility';
import { MemberDTO } from '../../types';
import { useSortedGroupedMembers } from './useSortedGroupedMembers';

type ManageUserViewProps = {
  projectId: number;
  members: MemberDTO[];
  reportsEnabled: boolean;
  onMemberVisibilityChanged?: () => void;
};

const getTooltip = ({ id, disabled }) => {
  if (disabled) {
    return {
      id,
      content: 'Not allowed. Report URL is empty.',
    };
  }

  return null;
};

export const ManageUserView = ({
  projectId,
  members,
  reportsEnabled,
  onMemberVisibilityChanged,
}: ManageUserViewProps) => {
  // Get project store values
  // TODO: Recheck logic here
  const { lookerReports, lookerReport, lookerReportUrl } = useSelector(
    (state: RootState) => state.project.Selected || {}
  );

  const hasResetRef = useRef(false);

  useEffect(() => {
    // If all report sources are empty, reset dashboardReportVisibility for members with true
    const isReportsEmpty =
      (!lookerReports || lookerReports.length === 0) &&
      (!lookerReport ||
        (Array.isArray(lookerReport) ? lookerReport.length === 0 : !lookerReport)) &&
      (!lookerReportUrl ||
        (Array.isArray(lookerReportUrl) ? lookerReportUrl.length === 0 : !lookerReportUrl));

    if (isReportsEmpty && Array.isArray(members)) {
      console.log('Resetting dashboard report visibility for all members');
      members.forEach((member) => {
        if (member['dashboard-reports-visible']) {
          // Only reset those currently set to true
          updateProjectMemberDashboardVisibilityMutation.mutate({
            projectMemberId: member.id,
            projectId,
            dashboardReportVisibility: false,
          });
        }
      });
      hasResetRef.current = true;
    } else if (!isReportsEmpty && hasResetRef.current) {
      // After reset, prevent restoring previous toggles by keeping hasResetRef true until next full delete
      // Do nothing here, toggles will remain off until user manually enables
    } else if (!isReportsEmpty) {
      hasResetRef.current = false;
    }
  }, [lookerReports, lookerReport, lookerReportUrl, members, projectId]);

  const dispatch = useDispatch();
  const updateProjectMemberDashboardVisibilityMutation =
    useUpdateProjectMemberDashboardVisibility();

  const handleToggleProjectMemberVisibility = async (
    projectMemberId: number,
    dashboardReportVisibility: boolean
  ) => {
    await updateProjectMemberDashboardVisibilityMutation.mutateAsync({
      projectMemberId,
      projectId,
      dashboardReportVisibility: !dashboardReportVisibility,
    });

    // Optimistic update
    queryClient.setQueryData(
      dashboardMembersKeys.list(+projectId),
      (oldData: DashboardMembersResponse) =>
        oldData
          ? {
              ...oldData,
              members: oldData?.members.map((m) => {
                if (m.id === projectMemberId) {
                  return {
                    ...m,
                    'dashboard-reports-visible': !dashboardReportVisibility,
                  };
                }

                return m;
              }),
            }
          : oldData
    );

    // Dispatch Redux action to update lookerReportUrl in project reducer (reducer now merges fields)
    dispatch({
      type: 'SET_PROJECT_SELECTED',
      Selected: {
        showLookerReport: !dashboardReportVisibility,
      },
    });

    if (onMemberVisibilityChanged) {
      onMemberVisibilityChanged();
    }
  };

  // Use custom hook for sorted/grouped members
  const sortedMembers = useSortedGroupedMembers(members);

  return (
    <div className="tw-overflow-auto tw-max-w-[780px]">
      <table className="tw-w-full tw-border-collapse">
        <thead className="tw-sticky tw-top-0 tw-z-[2] tw-bg-[#FAFAFA] tw-border tw-border-[#EEEEEE]">
          <tr>
            <th
              className={clsx(
                'tw-w-[35%] tw-py-2 tw-font-brandonTextMedium tw-text-[14px] tw-normal-case tw-tracking-normal tw-text-cfo-black tw-text-left tw-font-[420]'
              )}
            >
              Name
            </th>
            <th
              className={clsx(
                'tw-w-[35%] tw-py-2 tw-font-brandonTextMedium tw-text-[14px] tw-normal-case tw-tracking-normal tw-text-cfo-black tw-text-center tw-font-[420] '
              )}
            >
              User Role
            </th>
            <th
              className={clsx(
                'tw-w-[30%] tw-py-2 tw-font-brandonTextMedium tw-text-[14px] tw-normal-case tw-tracking-normal tw-text-cfo-black tw-text-left tw-font-[420]'
              )}
            ></th>
          </tr>
        </thead>

        <tbody className="tw-bg-white tw-border tw-border-[#EEEEEE]">
          {sortedMembers?.map((member) => {
            const isActive =
              reportsEnabled && lookerReports.length > 0
                ? member['dashboard-reports-visible']
                : false;
            const isDisabled = !reportsEnabled;
            return (
              <tr key={member.id}>
                <td
                  className={clsx(
                    'tw-p-[10px] tw-text-[#414A4F] tw-text-left tw-whitespace-pre-wrap tw-break-words tw-border-b tw-border-[#EEEEEE]'
                  )}
                >
                  {member['first-name'] + ' ' + member['last-name']}
                </td>
                <td
                  className={clsx(
                    'tw-p-[10px] tw-text-[#414A4F] tw-text-center tw-whitespace-pre-wrap tw-break-words tw-border-b tw-border-[#EEEEEE]'
                  )}
                >
                  {member.title}
                </td>
                <td className={clsx('tw-p-[10px] tw-text-center tw-border-b tw-border-[#EEEEEE]')}>
                  <Switch
                    className=""
                    isActive={isActive}
                    onChange={
                      isDisabled
                        ? undefined
                        : () =>
                            handleToggleProjectMemberVisibility(
                              member.id,
                              member['dashboard-reports-visible']
                            )
                    }
                    tooltip={getTooltip({
                      id: `du_${member.id}`,
                      disabled: !reportsEnabled,
                    })}
                    disabled={isDisabled || !reportsEnabled}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
